import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import PropTypes from 'prop-types';
import { Col, Form, Input, Row, Select, Spin } from 'antd';
import { useSelector } from 'react-redux';
import AssetLocalizationModal from '../modals/AssetLocalizationModal';
import useAssetsExtendedForm from '../../../Assets/components/hooks/useAssetsExtendedForm';
import useAssetType from '../../../Assets/components/hooks/useAssetType';
import { useAsset } from '../../hooks/useAsset';
import { useDictionaryTranslation } from '../../../../hooks/useDictionaryTranslation';
import { profileTypes } from '../../../../config/constants/userConstants';
import { statusAssets } from '../../../../config/constants/statusAssetsConstants';
import MobileForm from '../../../../components/mobileForm/MobileForm';
import { Button } from '../../../../components/buttons/buttons';
import { ExtendedFormProvider } from '../../../../components/mobileForm/context/mobileFormContext';
import { mobileFormTypes } from '../../../../components/mobileForm/hooks/useExtendedFormActions';
import { CustomSwitch } from '../../../../components/customSwitch/CustomSwitch';
import { SelectCompany } from '../../../../components/services/forms/SelectCompany';
import { FinalCustomerInput } from '../../../../components/finalCustomers/FinalCustomerInput/FinalCustomerInput';
import { AppointmentSelfSchedulingService } from '../../../../services/appointmentSelfSchedulingService';
import { ApplicationsService } from '../../../../services/applicationsService';
import { AssetsService } from '../../../../services/assetsService';
import { ErrorsModal } from '../../../../components/mobileForm/componentsMobileForm/ErrorsModal';
import SelectAssetType from '../../../../components/selectAssetType/SelectAssetType';

const AssetsDetailForm = ({
  assetId,
  basicInfoOnly,
  onFinish,
  idExtendedForm,
  assetTypeId,
  finalCustomerId,
  createAsset = AssetsService.createAsset,
  updateAssetFunction = AssetsService.updateAsset,
  enabledAssetType,
}) => {
  const history = useHistory();
  const { idAssetType } = useParams();
  const { assetData, isLoading, updateAsset, checkError } = useAsset({
    assetId,
    createAssetFunction: createAsset,
    updateAssetFunction,
  });
  const { getAssetType, dataAssetType } = useAssetType();
  const [form] = Form.useForm();
  const { translate: t } = useDictionaryTranslation();
  const [isEnableGeolocalization, setIsEnableGeolocalization] = useState(false);
  const [geolocalizationFromUbidots, setGeolocalizationFromUbidots] = useState(false);
  const [isOpenLocalizationModal, setIsOpenLocalizationModal] = useState(false);
  const [showErrorsModal, setShowErrorsModal] = useState(false);
  const [selectedFinalCustomer, setSelectedFinalCustomer] = useState();
  const extendedFormValues = useMemo(() => assetData?.extendedForm || [], [assetData]);
  const idApp = useMemo(() => dataAssetType?.idExtendedForm || '' || idExtendedForm, [dataAssetType]);
  const [selectedAssetType, setSelectedAssetType] = useState();

  const { profileData, accountModules, isSelfScheduling } = useSelector((state) => {
    return {
      isSelfScheduling: state.selfScheduling?.accountValidated,
      accountModules: state.auth.accountModules,
      profileData: state.auth.currentUser.profile,
    };
  });

  const formMobile = useAssetsExtendedForm({
    idApp,
    values: extendedFormValues,
    getApplicationForm: isSelfScheduling
      ? AppointmentSelfSchedulingService.getApplicationsById
      : ApplicationsService.getFormById,
  });

  const fieldsList = formMobile?.getFieldList();

  const canEditAssets =
    isSelfScheduling || ![profileTypes.OPERATOR.id].includes(profileData?.idProfile) || profileData?.canEditAssets;

  const enabledCompany = Form.useWatch('enabledCompany', form);
  const enabledFinalCustomer = Form.useWatch('enabledFinalCustomer', form);

  const saveAssetChanges = async ({ newAssetId, newValues, showMessage = true }) => {
    if (formMobile?.form?.dataForm) {
      const extendedFormData = await formMobile.getStructure(true, newAssetId);
      newValues ??= {};
      newValues.extendedForm = extendedFormData?.resultStructure;
    }
    if (!newValues) return null;
    return updateAsset(newValues, newAssetId, showMessage);
  };

  const submitForm = async () => {
    const resultValidations = formMobile?.values?.updateFormValues(true);
    if (resultValidations?.hasError) {
      setShowErrorsModal(true);
      return;
    }
    const values = form.getFieldsValue();

    const newValues = {
      ...values,
      status: values.status || 1,
      idCompany: values.enabledCompany ? values.idCompany?.id ?? null : null,
      idFinalCustomer: !finalCustomerId
        ? values.enabledFinalCustomer
          ? values.idFinalCustomer?._id ?? null
          : null
        : finalCustomerId,
      idAssetType: enabledAssetType ? selectedAssetType : assetId ? assetData?.idAssetType : idAssetType || assetTypeId,
    };
    try {
      if (!assetId) {
        const { asset } = await createAsset(newValues);
        await saveAssetChanges({
          newAssetId: asset._id,
          showMessage: false,
        });
        onFinish(true);
      } else {
        await saveAssetChanges({
          newAssetId: assetId,
          newValues,
          showMessage: true,
        });
        history.push(`/asset/${assetData.idAssetType}`);
      }
    } catch (error) {
      const messageError = error?.response?.data?.message;
      checkError(messageError);
    }
  };

  const handleCloseLocalizationModal = (address) => {
    try {
      if (address?.markerCoords) {
        form.setFieldValue('geoLocalization', address);
      }
      setIsOpenLocalizationModal(false);
    } catch (error) {
      console.error(error);
    }
  };

  const onChangeFinalCustomer = (item) => {
    setSelectedFinalCustomer(item);
    form.setFieldsValue({ idFinalCustomer: item ?? null });
    if (item && !form.getFieldValue('enabledFinalCustomer')) {
      form.setFieldsValue({ enabledFinalCustomer: true });
    }
  };

  const onChangeAssetType = (data) => {
    setSelectedAssetType(data.value);
  };

  const closeErrorsModal = () => {
    setShowErrorsModal(false);
  };

  useEffect(() => {
    const typeId = enabledAssetType ? selectedAssetType : assetId ? assetData?.idAssetType : idAssetType;
    if (typeId != null) {
      getAssetType(typeId);
    }
  }, [assetId, idAssetType, assetData, selectedAssetType]);

  useEffect(() => {
    if (assetId || selectedAssetType) {
      form.setFieldsValue({
        reference: assetData?.reference,
        name: assetData?.name,
        status: assetData?.status,
        description: assetData?.description || '',
        geoLocalization: assetData?.geoLocalization || null,
        deviceCode: assetData?.deviceCode || null,
        idCompany: assetData?.companyData ?? null,
        idFinalCustomer: assetData?.finalCustomerData ?? null,
        enabledCompany: !!assetData?.companyData,
        enabledFinalCustomer: !!assetData?.finalCustomerData,
      });
    } else {
      form.setFieldsValue({
        enabledCompany: false,
        enabledFinalCustomer: false,
      });
    }

    setSelectedFinalCustomer(assetData?.finalCustomerData);
  }, [assetData, assetTypeId, selectedAssetType]);

  useEffect(() => {
    if (
      accountModules?.assetGeolocalization &&
      dataAssetType?.isGeolocationEnabled &&
      !dataAssetType?.isUbidotsLocationEnabled
    ) {
      setIsEnableGeolocalization(true);
      setGeolocalizationFromUbidots(false);
    }
    if (
      accountModules?.assetGeolocalization &&
      dataAssetType?.isGeolocationEnabled &&
      dataAssetType?.isUbidotsLocationEnabled
    ) {
      setGeolocalizationFromUbidots(true);
    }
  }, [dataAssetType, accountModules]);

  return (
    <Spin spinning={isLoading}>
      <Form form={form} name="assetInfoForm" onFinish={submitForm} layout="vertical">
        <Row gutter={20}>
          {!enabledAssetType ? null : (
            <Col xs={24} md={24}>
              <Form.Item
                name="selectedAsset"
                label={t('assetTypes')}
                rules={[{ message: t('assetRequiredField'), required: true }]}
                style={{ marginBottom: '6px' }}
              >
                <SelectAssetType placeholder={t('assetTypes')} onChange={onChangeAssetType} width />
              </Form.Item>
            </Col>
          )}

          {!basicInfoOnly ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="reference"
                label={t('assetReference')}
                rules={[{ required: true, message: t('referenceRequired') }]}
              >
                <Input disabled={!canEditAssets} />
              </Form.Item>
            </Col>
          ) : null}
          <Col xs={24} md={basicInfoOnly ? 24 : 12}>
            <Form.Item name="name" label={t('name')} rules={[{ required: true, message: t('notifyToRequired') }]}>
              <Input disabled={!canEditAssets} />
            </Form.Item>
          </Col>
          {!basicInfoOnly ? (
            <>
              <Col xs={24} md={12}>
                <Form.Item
                  name="status"
                  label={t('status')}
                  rules={[{ required: true, message: t('notifyToRequired') }]}
                >
                  <Select disabled={!canEditAssets}>
                    {Object.values(statusAssets).map((status) => (
                      <Select.Option key={status.id} value={status.id}>
                        {t(status.description)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={24}>
                <Form.Item name="description" label={t('description')}>
                  <Input disabled={!canEditAssets} />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Row>
                  <Col xs={24} md={24}>
                    <Form.Item
                      name="enabledCompany"
                      rules={[{ type: 'switch' }]}
                      label={t('assignedCompanyText')}
                      valuePropName="checked"
                    >
                      <CustomSwitch
                        onChange={(checked) => {
                          if (!checked) {
                            form.setFieldsValue({ idCompany: null });
                          }
                        }}
                        checkedText={t('yes')}
                        unCheckedText={t('no')}
                        disabled={!canEditAssets}
                      />
                    </Form.Item>
                  </Col>

                  <Form.Item noStyle>
                    {enabledCompany ? (
                      <Col xs={24} md={24}>
                        <Form.Item
                          name="idCompany"
                          rules={[{ required: true, message: t('customerRequired') }]}
                          label={t('customer')}
                          valuePropName="selectedCompany"
                        >
                          <SelectCompany disabled={!canEditAssets} />
                        </Form.Item>
                      </Col>
                    ) : null}
                  </Form.Item>
                </Row>
              </Col>

              <Col xs={24} md={12}>
                <Row>
                  <Col xs={24} md={24}>
                    <Form.Item
                      name="enabledFinalCustomer"
                      rules={[{ type: 'switch' }]}
                      label={t('assignedFinalCustomerText')}
                      valuePropName="checked"
                    >
                      <CustomSwitch
                        onChange={(checked) => {
                          if (!checked) {
                            form.setFieldsValue({ idFinalCustomer: null });
                            setSelectedFinalCustomer(undefined);
                          }
                        }}
                        checkedText={t('yes')}
                        unCheckedText={t('no')}
                        disabled={!canEditAssets}
                      />
                    </Form.Item>
                  </Col>

                  <Form.Item noStyle>
                    {enabledFinalCustomer ? (
                      <Col xs={24} md={24}>
                        <FinalCustomerInput
                          name="idFinalCustomer"
                          value={selectedFinalCustomer}
                          onChange={onChangeFinalCustomer}
                          label="finalCustomer"
                          disabledEdit
                        />
                      </Col>
                    ) : null}
                  </Form.Item>
                </Row>
              </Col>
            </>
          ) : null}

          {isEnableGeolocalization && !geolocalizationFromUbidots && (
            <Col xs={12} md={12}>
              <Form.Item name="geoLocalization" noStyle />
              <Button type="secondary" onClick={() => setIsOpenLocalizationModal(true)} disabled={!canEditAssets}>
                {t('selectLocalization')}
              </Button>
            </Col>
          )}

          {geolocalizationFromUbidots && (
            <Col xs={24} md={24}>
              <Form.Item name="deviceCode" label={t('ID')} rules={[{ required: true }]}>
                <Input disabled={!canEditAssets} />
              </Form.Item>
            </Col>
          )}

          <Col md={24} style={{ marginTop: '20px' }}>
            <ExtendedFormProvider itemId={assetId} itemType={mobileFormTypes.asset}>
              <MobileForm
                fields={fieldsList}
                formValues={formMobile.values.mobileFormValues}
                onChange={formMobile.values.updateFieldValue}
              />
            </ExtendedFormProvider>
          </Col>
        </Row>

        {canEditAssets && !basicInfoOnly ? (
          <Row justify="end">
            <Button type="primary" htmlType="submit">
              {t('save')}
            </Button>
          </Row>
        ) : null}
      </Form>

      {isOpenLocalizationModal && (
        <AssetLocalizationModal
          handleClose={handleCloseLocalizationModal}
          show={isOpenLocalizationModal}
          assetCoordinates={form.getFieldValue('geoLocalization')}
        />
      )}

      {showErrorsModal && formMobile.errors?.formErrors?.length ? (
        <ErrorsModal show={showErrorsModal} errors={formMobile.errors?.formErrors} onClose={closeErrorsModal} />
      ) : null}
    </Spin>
  );
};

AssetsDetailForm.propTypes = {
  assetId: PropTypes.string,
  basicInfoOnly: PropTypes.bool,
  onFinish: PropTypes.func,
  idExtendedForm: PropTypes.string,
  assetTypeId: PropTypes.string,
  finalCustomerId: PropTypes.string,
  createAsset: PropTypes.func,
  updateAssetFunction: PropTypes.func,
  enabledAssetType: PropTypes.bool,
};

export default AssetsDetailForm;
